import { ComposedAlert, ComposedCard, FancyLink, Page, Stack, Text } from '@eigtech/ui-shared-dave'
import { createForm } from '@eigtech/ui-shared-forms'
import { createFileRoute } from '@tanstack/react-router'
import { useRef } from 'react'
import { z } from 'zod'

const SearchSchema = z.object({
  auth0_domain: z.string().catch(''),
  state: z.string().catch(''),
  roles: z.string().catch(''),
})

export const Route = createFileRoute('/consent')({
  component: UserConsent,
  validateSearch: SearchSchema.parse,
})

function UserConsent() {
  // these are added as a part of the "Redirect to consent form" action in the auth0 "Login" flow
  // https://manage.auth0.com/dashboard/us/eigtech/actions/library/details/68800328-e141-4738-bea6-85301bb114bc
  const { auth0_domain: auth0Domain, state } = Route.useSearch()

  const form = useForm()

  const isError = !(auth0Domain && state)

  const auth0FormRef = useRef<HTMLFormElement>(null)

  function onSubmit() {
    if (!auth0FormRef.current) return
    auth0FormRef.current.submit()
  }

  return (
    <Page containerProps={{ maxW: 'container.text', py: 8 }}>
      {isError ? (
        <ComposedAlert
          alert={{
            title: 'Something went wrong.',
            description: 'Required context is missing. Please return to login form and try again.',
          }}
          status="error"
        />
      ) : (
        <>
          <ComposedCard>
            <form.Form onSubmit={onSubmit}>
              <Stack spacing="4">
                <Text>
                  In order to use the EIG Portal, please agree to our{' '}
                  <FancyLink href="https://evansclaims.com/terms-of-service/" isExternal>
                    Terms & Conditions
                  </FancyLink>{' '}
                  and{' '}
                  <FancyLink href="https://evansclaims.com/privacy-policy/" isExternal>
                    Privacy Policy
                  </FancyLink>
                  .
                </Text>

                <CheckboxField
                  label="I agree to the Terms & Conditions and the Privacy Policy."
                  name="consent"
                />
                <CheckboxField
                  helperText="I agree to receive SMS text messages for important notifications and meeting invitations from EIG Claims Services (Data rates may apply)"
                  label="SMS Text notifications"
                  name="smsConsent"
                />

                <form.SubmitButton />
              </Stack>
            </form.Form>
          </ComposedCard>

          {/* this form is used to redirect user to auth0 once they have agreed to terms/privacy */}
          <form
            ref={auth0FormRef}
            action={`https://${auth0Domain}/continue?state=${state}`}
            method="post"
            style={{ display: 'none' }}
          >
            <input name="confirm" readOnly value="yes" />
          </form>
        </>
      )}
    </Page>
  )
}

const defaultError =
  'In order to continue, please agree to the Terms & Conditions and Privacy Policy.'
const smsConsentError = 'In order to continue, please agree to SMS Text notifications.'

const Schema = z.object({
  consent: z.literal(true, {
    error: () => defaultError,
  }),
  smsConsent: z.literal(true, {
    error: () => smsConsentError,
  }),
})

const { useForm, CheckboxField } = createForm(Schema, 'consentForm')
